import React from "react";
import {
  ArrowRight,
  Box,
  Database,
  Eye,
  Brain,
  Radio,
  Cpu,
  Camera,
  Monitor,
} from "lucide-react";

export const Factory360ProcessProducts = () => {
  const processSteps = [
    {
      num: "01",
      title: "Sensors",
      icon: (
        <svg
          className="w-6 h-6"
          viewBox="0 0 24 24"
          fill="none"
          stroke="url(#brandGradient)"
          strokeWidth="2.2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M4.9 19.1C1 15.2 1 8.8 4.9 4.9" />
          <path d="M7.8 16.2c-2.3-2.3-2.3-6.1 0-8.5" />
          <circle cx="12" cy="12" r="2" fill="url(#brandGradient)" />
          <path d="M16.2 7.8c2.3 2.3 2.3 6.1 0 8.5" />
          <path d="M19.1 4.9C23 8.8 23 15.2 19.1 19.1" />
        </svg>
      ),
    },
    {
      num: "02",
      title: "Identify & Track",
      icon: (
        <Box
          className="w-6 h-6"
          stroke="url(#brandGradient)"
          strokeWidth={2.2}
        />
      ),
    },
    {
      num: "03",
      title: "Capture & Connect",
      icon: (
        <Database
          className="w-6 h-6"
          stroke="url(#brandGradient)"
          strokeWidth={2.2}
        />
      ),
    },
    {
      num: "04",
      title: "See & Understand",
      icon: (
        <Eye
          className="w-6 h-6"
          stroke="url(#brandGradient)"
          strokeWidth={2.2}
        />
      ),
    },
    {
      num: "05",
      title: "Intelligence Layer",
      icon: (
        <Brain
          className="w-6 h-6"
          stroke="url(#brandGradient)"
          strokeWidth={2.2}
        />
      ),
    },
  ];

  const products = [
    {
      title: "Data Loggers",
      desc: "Capture machine, equipmen and environmental data from the factory floor.",
      icon: (
        <Database
          className="w-4 h-4"
          stroke="url(#brandGradient)"
          strokeWidth={2.2}
        />
      ),
      img: "/images/physical-ai/prod-datalogger.png",
    },
    {
      title: "RFID Solutions",
      desc: "Track materials, assets, inventory and work-in-progress across operations.",
      icon: (
        <Radio
          className="w-4 h-4"
          stroke="url(#brandGradient)"
          strokeWidth={2.2}
        />
      ),
      img: "/images/physical-ai/prod-rfid.png",
    },
    {
      title: "Sensors",
      desc: "Monitor condition, movemen environment, energy and process parameters.",
      icon: (
        <Cpu
          className="w-4 h-4"
          stroke="url(#brandGradient)"
          strokeWidth={2.2}
        />
      ),
      img: "/images/physical-ai/prod-sensors.png",
    },
    {
      title: "Vision Solutions",
      desc: "AI-powered visual inspection, monitoring, tracking and process intelligence.",
      icon: (
        <Camera
          className="w-4 h-4"
          stroke="url(#brandGradient)"
          strokeWidth={2.2}
        />
      ),
      img: "/images/physical-ai/prod-vision.png",
    },
    {
      title: "HMI Solutions",
      desc: "Human-Machine Interface for real-time visibility, control and collaboration on the factory floor.",
      icon: (
        <Monitor
          className="w-4 h-4"
          stroke="url(#brandGradient)"
          strokeWidth={2.2}
        />
      ),
      img: "/images/physical-ai/prod-hmi.png",
    },
  ];

  return (
    <section
      className="relative w-full overflow-hidden py-14 sm:py-18 lg:py-20"
      style={{ backgroundColor: "rgba(245, 251, 254, 1)" }}
    >
      {/* Global SVG Definitions for Brand Gradient */}
      <svg width="0" height="0" className="absolute pointer-events-none">
        <defs>
          <linearGradient id="brandGradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="rgba(5, 214, 218, 1)" />
            <stop offset="100%" stopColor="rgba(49, 112, 247, 1)" />
          </linearGradient>
        </defs>
      </svg>

      <div className="mx-auto max-w-[1440px] px-6 lg:px-[60px] xl:px-[70px]">
        {/* Top Section: THE FACTORY 360 PROCESS */}
        <div className="w-full text-center">
          <p className="text-[11px] sm:text-[11.5px] font-bold uppercase tracking-[2.8px] text-[#6B7280]">
            THE FACTORY 360 PROCESS
          </p>

          {/* 5-Step Process Flow with Connecting Arrows aligned with circles */}
          <div className="mt-8 sm:mt-10 flex items-start justify-center overflow-x-auto pb-2">
            {processSteps.map((step, idx) => (
              <React.Fragment key={step.num}>
                {/* Step Item Column */}
                <div className="flex flex-col items-center w-[116px] sm:w-[136px] lg:w-[155px] xl:w-[170px] shrink-0 text-center group">
                  {/* Circular Icon Container with exact rgba(228, 243, 255, 1) */}
                  <div
                    className="flex h-[56px] w-[56px] sm:h-[62px] sm:w-[62px] items-center justify-center rounded-full transition-transform duration-300 group-hover:scale-105"
                    style={{ backgroundColor: "rgba(228, 243, 255, 1)" }}
                  >
                    {step.icon}
                  </div>

                  {/* Step Number in Brand Gradient */}
                  <span
                    className="mt-2.5 sm:mt-3 text-[12.5px] sm:text-[13px] font-bold"
                    style={{
                      background:
                        "linear-gradient(90deg, rgba(5, 214, 218, 1) 0%, rgba(49, 112, 247, 1) 100%)",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                    }}
                  >
                    {step.num}
                  </span>

                  {/* Step Title */}
                  <span className="mt-0.5 text-[14px] sm:text-[15px] font-bold text-[#0A1629] whitespace-nowrap">
                    {step.title}
                  </span>
                </div>

                {/* Connecting Arrow between steps - vertically aligned with the circle */}
                {idx !== processSteps.length - 1 && (
                  <div className="flex h-[56px] sm:h-[62px] items-center justify-center px-1 sm:px-2.5 lg:px-4 shrink-0">
                    <ArrowRight
                      className="w-4 h-4 sm:w-5 sm:h-5 text-[#8E9CAE]"
                      strokeWidth={1.8}
                    />
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Bottom Section: PRODUCTS */}
        <div className="mt-14 sm:mt-16">
          {/* Section Tag: PRODUCTS */}
          <p className="text-[11px] sm:text-[11.5px] font-bold uppercase tracking-[2.6px] text-[#6B7280]">
            PRODUCTS
          </p>

          {/* 5 Hardware Product Cards Grid */}
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3.5 xl:gap-4">
            {products.map((product) => (
              <div
                key={product.title}
                className="group relative flex flex-col justify-between rounded-[16px] sm:rounded-[18px] border border-[#E2E8F0] bg-white p-3.5 sm:p-4 shadow-[0_2px_10px_rgba(0,0,0,0.02)] transition-all duration-300 hover:-translate-y-1 hover:shadow-md hover:border-[#BAE6FD]"
              >
                {/* Top: Icon Badge + Title */}
                <div className="flex items-center gap-2 sm:gap-2.5">
                  <div
                    className="flex h-7 w-7 sm:h-8 sm:w-8 shrink-0 items-center justify-center rounded-[8px]"
                    style={{ backgroundColor: "rgba(228, 243, 255, 1)" }}
                  >
                    {product.icon}
                  </div>
                  <h3 className="text-[14px] sm:text-[14.5px] lg:text-[15px] font-bold text-[#0A1629] whitespace-nowrap">
                    {product.title}
                  </h3>
                </div>

                {/* Lower Row: Left Description + Right Prominent Image */}
                <div className="mt-3 flex items-end justify-between min-h-[76px] sm:min-h-[82px] relative overflow-hidden">
                  <p className="text-[11px] sm:text-[11.5px] leading-[15px] sm:leading-[16px] text-[#64748B] w-[53%] sm:w-[55%] self-start pr-1">
                    {product.desc}
                  </p>

                  <div className="absolute right-0 bottom-0 w-[47%] sm:w-[45%] h-[76px] sm:h-[84px] lg:h-[88px] flex items-end justify-end pointer-events-none">
                    <img
                      src={product.img}
                      alt={product.title}
                      className="w-full h-full object-contain object-bottom-right transition-transform duration-300 group-hover:scale-105"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Factory360ProcessProducts;
